import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AppService, getLanguage } from '../../app.service';
import { environment } from '../../../environments/environment';
import { DataService } from '../../data-service';
import { ImageObjectSchema } from '@upupa/common';

@Injectable({
    providedIn: 'root',
})
export class ArtGalleryService {
    private readonly dataService = inject(DataService);
    private readonly app = inject(AppService);
    loading = signal(false);
    page = 1;
    canGetMore = signal(true);
    totalPages = 0;

    private readonly _all = signal<any[]>([]);
    all = computed(() => {
        return this._all();
    });

    rerender() {
        const __all = this.all()
            .slice()
            .map((a) => ({ ...a, rerendered: false }));
        this._all.set(__all);
    }
    reset() {
        this._all.set([]);
        this.canGetMore.set(true);
        this.totalPages = 0;
        this.page = 1;
    }

    async getNextPage() {
        if (this.totalPages > 0 && this.page + 1 > this.totalPages) return;
        this.page = Math.min(this.page + 1, this.totalPages);
        this.getGalleryItems();
    }

    filterItems = signal<any[]>([]);
    private _category = '';
    public get category(): string {
        return this._category;
    }
    public set category(v: string) {
        if (this._category === v) return;
        this._category = v;
        this.app.getGalleryFilter(v).then((res) => this.filterItems.set(res));
    }

    filter: string | undefined = undefined;

    change_filter(v: string) {
        v = v ?? '';
        if (this.filter === v) return;
        const possibleFilters = this.filterItems();
        if (possibleFilters.indexOf(v) == -1) this.filter = undefined;
        this.filter = v;
    }

    resetAndGetItems() {
        this.reset();
        this.getGalleryItems();
    }

    private readonly injector = inject(Injector);
    private readonly artBoardSelect = () => `title,slug,category,date,largeImage,mediumImage,smallImage,translations`;

    async findIndexOrGetItemBySlug(slug: string): Promise<number> {
        const a = this.all().find((i) => i.slug === slug);
        if (a) return Promise.resolve(a.index);
        const item = await this.getGalleryItem(slug);
        return item.index;
    }

    async getGalleryItem(slug: string) {
        await this._requestArtBoard({ slug: `{eq}${slug}` });
        return this.all().find((i) => i.slug === slug);
    }

    async getGalleryItems() {
        const query = {
            page: this.page,
            category: this.category,
            per_page: environment.gallery.pageSize,
            sort_by: 'order,desc',
        };
        if (this.filter?.length > 0) query['filter'] = `{in}${this.filter}`;

        try {
            const pageData = await this._requestArtBoard(query);

            this.totalPages = Math.ceil(pageData.total / environment.gallery.pageSize);
            this.canGetMore.set(this.page < this.totalPages);
        } catch (err) {
            console.error(err);
        }
    }

    private async _requestArtBoard(query: Record<string, any>): Promise<any> {
        const pageData = await this.dataService.get<any>(`/artBoards?select=${this.artBoardSelect()}`, {
            ...query,
        });

        pageData.data = this.updateAll(pageData.data);
        return pageData;
    }

    private updateAll(items: any): any[] {
        const lang = getLanguage(this.injector);
        const all = this._all();
        const currLen = all.length;

        const unique = items
            .filter((i) => all.findIndex((a) => a._id === i._id) === -1)
            .map((a, i) => {
                const meta = { ...a.seo, ...a?.translations?.[lang]?.seo };
                const imagePath = a.largeImage.path || a.mediumImage.path || a.smallImage.path || 'category/paint.jpg';

                meta['content-language'] = meta['content-language'] || lang;
                meta.type = meta.type || 'article';

                meta.image = meta.image || imagePath; // ogImage
                meta.og ??= {};
                meta.og['og:image'] = meta.og?.['og:image'] || imagePath || meta.image;

                meta.twitter ??= {};
                meta.twitter['twitter:image'] = meta.twitter?.['twitter:image'] || imagePath || meta.image;
                meta.twitter['twitter:card'] = meta.twitter?.['twitter:card'] || 'summary_large_image';

                meta.title ??= meta.title || a.title || a.slug;
                meta.twitter['twitter:title'] = meta.twitter?.['twitter:title'] || a.title || a.slug || meta.title;
                meta.twitter['twitter:description'] = meta.twitter?.['twitter:description'] || meta.description;
                meta.og['og:title'] = meta.og?.['og:title'] || a.title || a.slug || meta.title;
                meta.og['og:description'] = meta.og?.['og:description'] || meta.description;

                meta.schema ??= {};
                meta.schema['imageObject'] = {
                    url: environment.base_url + '/' + meta.image,
                    contentUrl: environment.base_url + '/' + meta.image,
                    width: a.largeImage.width,
                    height: a.largeImage.height,
                    '@type': 'ImageObject',
                    '@context': 'http://schema.org',
                    author: meta.author,
                    datePublished: a.date,
                    description: meta.description,
                    license: 'https://creativecommons.org/licenses/by/4.0/',
                    name: meta.title,
                    uploadDate: a.date,
                    ...meta.schema['imageObject'],
                } as ImageObjectSchema;
                return {
                    ...a,
                    ...(a.translations?.[lang] ?? {}),
                    index: currLen + i,
                    meta,
                };
            });

        this._all.update((a) => {
            a.push(...unique);
            return a.slice();
        });

        return unique;
    }
}
